// ─── src/components/layout/Header.tsx ─────────────────────────────────────────
import type { FC } from "react";
import { RefreshCw, LogOut, MapPin, Shield, Menu } from "lucide-react";
import { useAuth } from "../../auth/AuthContext";

interface HeaderProps {
  onRefresh:    () => void;
  isRefreshing: boolean;
  /** Only passed when a sidebar exists (master). Mobile hamburger opens it. */
  onMenuClick?: () => void;
}

export const Header: FC<HeaderProps> = ({ onRefresh, isRefreshing, onMenuClick }) => {
  const { user, logout } = useAuth();

  return (
    <header className="border-b border-brand-border bg-brand-card/80
                       backdrop-blur-sm sticky top-0 z-40 px-6 py-4">
      <div className="max-w-screen-xl mx-auto flex items-center justify-between">
        {/* Brand */}
        <div className="flex items-center gap-3">
          {onMenuClick && (
            <button
              onClick={onMenuClick}
              className="lg:hidden p-1.5 rounded-lg text-brand-muted hover:text-white hover:bg-brand-border/50"
              aria-label="Open navigation"
            >
              <Menu size={18} />
            </button>
          )}
          <div className="w-8 h-8 rounded-lg bg-brand-orange flex items-center
                          justify-center text-white font-bold text-sm shrink-0">
            G
          </div>
          <div>
            <h1 className="text-white font-bold text-base leading-none">
              SW Syria 2026
            </h1>
            <p className="text-brand-muted text-xs mt-0.5">Applicants Dashboard</p>
          </div>
        </div>

        {/* Right side */}
        <div className="flex items-center gap-3">
          {/* Current user badge */}
          {user && (
            <div className="hidden sm:flex items-center gap-2 px-3 py-1.5
                            bg-brand-border/50 rounded-lg border border-brand-border">
              {user.role === "master" ? (
                <Shield size={13} className="text-brand-orange" />
              ) : (
                <MapPin size={13} className="text-brand-tealLight" />
              )}
              <span className="text-white text-sm font-medium">{user.label}</span>
              {user.role === "master" && (
                <span className="text-xs text-brand-orange bg-brand-orange/10
                                 px-1.5 py-0.5 rounded-full border border-brand-orange/20">
                  Master
                </span>
              )}
            </div>
          )}

          <button
            onClick={onRefresh}
            disabled={isRefreshing}
            className="flex items-center gap-2 btn-ghost text-sm"
            aria-label="Refresh data"
          >
            <RefreshCw size={15} className={isRefreshing ? "animate-spin" : ""} />
            <span className="hidden sm:inline">Refresh</span>
          </button>

          <button
            onClick={logout}
            className="flex items-center gap-2 btn-ghost text-sm
                       hover:text-red-400 hover:border-red-400/30"
            aria-label="Sign out"
          >
            <LogOut size={15} />
            <span className="hidden sm:inline">Sign out</span>
          </button>
        </div>
      </div>
    </header>
  );
};
